"use client";

import { useState, useEffect } from "react";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserServiceCommissions,
  createUserServiceCommission,
  updateUserServiceCommission,
  deleteUserServiceCommission,
} from "@/store/modules/userServiceCommission/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { fetchEmployees } from "@/store/modules/employees/thunks";
import { toast } from "react-toastify";

const userServiceCommissionSchema = Yup.object().shape({
  user_id: Yup.string().required("Funcionário é obrigatório"),
  service_id: Yup.string().required("Serviço é obrigatório"),
  commission: Yup.number()
    .transform((value: any, originalValue: any) => {
      // Accept values like "40%" or formatted strings and convert to number
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue === ""
      )
        return 0;
      if (typeof originalValue === "string") {
        const cleaned = originalValue
          .replace(/[^0-9,.-]/g, "")
          .replace(",", ".");
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
      }
      return value;
    })
    .required("Comissão é obrigatória")
    .min(0, "Comissão não pode ser negativa")
    .max(100, "Comissão não pode ser maior que 100")
    .typeError("Comissão deve ser um número"),
});

const useUserServiceCommission = (
  serviceId?: number,
  onSuccess?: () => void,
  skipAutoFetch: boolean = false,
) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { hasPermission } = usePermission();
  const [userServiceCommissionId, setUserServiceCommissionId] = useState<
    number | null
  >(null);

  // Cache inteligente
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(userServiceCommissionSchema),
    defaultValues: {
      user_id: "",
      service_id: serviceId?.toString() || "",
      commission: 0,
    },
  });

  const setFormData = (
    data: Partial<typeof userServiceCommissionSchema.__outputType>,
  ) => {
    reset(data);
  };

  const onSubmit = async (
    data: typeof userServiceCommissionSchema.__outputType,
  ) => {
    try {
      // Ensure commission is a plain number (accept strings like "40%" from masked inputs)
      let commissionValue: number = 0;
      try {
        const rawCommission: any = (data as any).commission;
        if (
          rawCommission === undefined ||
          rawCommission === null ||
          rawCommission === ""
        ) {
          commissionValue = 0;
        } else if (typeof rawCommission === "string") {
          const cleaned = rawCommission
            .replace(/[^0-9,.-]/g, "")
            .replace(",", ".");
          commissionValue = parseFloat(cleaned) || 0;
        } else {
          commissionValue = Number(rawCommission) || 0;
        }
      } catch (e) {
        commissionValue = 0;
      }

      const payload = {
        ...data,
        service_id: serviceId || parseInt(data.service_id),
        commission: commissionValue,
      };

      const result = await dispatch(createUserServiceCommission(payload));

      if (createUserServiceCommission.fulfilled.match(result)) {
        toast.success("Comissão diferenciada criada com sucesso!");

        const searchParams = serviceId
          ? {
              ...params,
              filter: {
                ...params.filter,
                service_id: serviceId,
              },
            }
          : params;
        dispatch(fetchUserServiceCommissions(searchParams));

        if (onSuccess) onSuccess();
        reset();
        return true;
      } else {
        console.error("Erro na operação:", result);
        toast.error("Erro ao criar comissão diferenciada");
        return false;
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
      toast.error("Erro ao enviar dados");
      return false;
    }
  };

  const onUpdate = async (
    commissionId: number,
    data: typeof userServiceCommissionSchema.__outputType,
  ) => {
    try {
      // Normalize commission similar to create
      let commissionValue: number = 0;
      try {
        const rawCommission: any = (data as any).commission;
        if (
          rawCommission === undefined ||
          rawCommission === null ||
          rawCommission === ""
        ) {
          commissionValue = 0;
        } else if (typeof rawCommission === "string") {
          const cleaned = rawCommission
            .replace(/[^0-9,.-]/g, "")
            .replace(",", ".");
          commissionValue = parseFloat(cleaned) || 0;
        } else {
          commissionValue = Number(rawCommission) || 0;
        }
      } catch (e) {
        commissionValue = 0;
      }

      const payload = {
        ...data,
        service_id: serviceId || parseInt(data.service_id),
        commission: commissionValue,
      };

      const result = await dispatch(
        updateUserServiceCommission({
          id: commissionId,
          data: payload,
        }),
      );

      if (updateUserServiceCommission.fulfilled.match(result)) {
        toast.success("Comissão diferenciada atualizada com sucesso!");

        const searchParams = serviceId
          ? {
              ...params,
              filter: {
                ...params.filter,
                service_id: serviceId,
              },
            }
          : params;
        dispatch(fetchUserServiceCommissions(searchParams));

        if (onSuccess) onSuccess();
        reset();
        return true;
      } else {
        console.error("Erro na operação:", result);
        toast.error("Erro ao atualizar comissão diferenciada");
        return false;
      }
    } catch (error) {
      console.error("Erro ao atualizar dados:", error);
      toast.error("Erro ao atualizar dados");
      return false;
    }
  };

  const handleDelete = async (idToDelete?: number) => {
    const targetId = idToDelete || userServiceCommissionId;
    if (!targetId) return false;

    try {
      const result = await dispatch(deleteUserServiceCommission(targetId));

      if (deleteUserServiceCommission.fulfilled.match(result)) {
        const searchParams = serviceId
          ? {
              ...params,
              filter: {
                ...params.filter,
                service_id: serviceId,
              },
            }
          : params;
        dispatch(fetchUserServiceCommissions(searchParams));

        setUserServiceCommissionId(null);
        return true;
      } else {
        console.error("Erro ao excluir:", result);
        return false;
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      toast.error("Erro ao excluir comissão diferenciada");
      return false;
    }
  };

  const {
    userServiceCommissions,
    loading,
    error,
    fetched: userServiceCommissionsFetched,
  } = useSelector((state: any) => state.userServiceCommission);
  const { employees, fetched: employeesFetched } = useSelector(
    (state: any) => state.employees,
  );
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    const searchParams = serviceId
      ? {
          ...params,
          filter: {
            ...params.filter,
            service_id: serviceId,
          },
        }
      : params;

    const shouldFetchCommissions =
      !userServiceCommissionsFetched &&
      hasPermission("read userServiceCommission");
    const shouldFetchEmployees =
      !employeesFetched && hasPermission("read user");

    if (shouldFetchCommissions) {
      dispatch(fetchUserServiceCommissions(searchParams));
    }

    if (shouldFetchEmployees) {
      dispatch(fetchEmployees(params));
    }
  }, [params, serviceId, userServiceCommissionsFetched, employeesFetched]);

  const handleFilterChange = (value: string) => {
    const newParams = { ...params, search: value, page: 1 };
    setParams(newParams);
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    onUpdate,
    errors,
    isSubmitting,
    setFormData,
    reset,
    userServiceCommissions,
    employees,
    loading,
    error,
    handleDelete,
    setUserServiceCommissionId,
    handleFilterChange,
    hasPermission,
  };
};

export default useUserServiceCommission;
